import { useGlobalUser } from '@sparknz/journeys-common';
import { useSnackbar } from '@sparknz/spark-pattern-library';
import { requestGuestLogin } from '@sparknz/universal-login-library';
import { operations } from '@sparknz/universal-login-library/types/generated/typings/unifiedLoginService';

import {
  GENERIC_DEFAULT_ERROR_CODE,
  GENERIC_DEFAULT_ERROR_MESSAGE,
} from 'shared/constants/errorMessages.constants';
import { useCaptcha } from 'shared/providers';
import { trackFailedLoginEvent } from 'shared/utils/loginTracking.utils';

// all error type scenarios have same schema (401, 500 etc);
type ErrorResponse = operations['guestLogin']['responses']['400']['schema'];

interface GuestLogin {
  tryGuestLogin: (options?: GuestLoginOptions) => Promise<{ success: boolean }>;
}

interface GuestLoginOptions {
  shouldThrowError?: boolean;
}

function useGuestLogin(): GuestLogin {
  const { getToken } = useCaptcha();
  const { refetchUser } = useGlobalUser();
  const { showSnackbar } = useSnackbar();

  const tryGuestLogin = async ({
    shouldThrowError = false,
  }: GuestLoginOptions = {}) => {
    try {
      const token = await getToken();

      await requestGuestLogin({
        captchaToken: token,
        redirectURL: window?.location.href,
      });

      refetchUser();
      return { success: true };
    } catch (error) {
      const errorResponse = error as ErrorResponse;
      const errorMessageObject = errorResponse?.messages?.[0];

      const message =
        errorMessageObject?.message ?? GENERIC_DEFAULT_ERROR_MESSAGE;
      const code =
        errorMessageObject?.code?.toString() ?? GENERIC_DEFAULT_ERROR_CODE;

      trackFailedLoginEvent({
        errorMessage: message,
        internalErrorCode: code,
        internalErrorMessage: GENERIC_DEFAULT_ERROR_MESSAGE,
      });

      if (shouldThrowError) {
        throw error;
      }

      showSnackbar({
        text: `${message}`,
        variant: 'error',
      });

      return { success: false };
    }
  };

  return { tryGuestLogin };
}

export default useGuestLogin;
